import { Button } from "components/ui/button";
import { Calendar } from "components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { CalendarDays, ChevronDown } from "lucide-react";
import { useState } from "react";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import type { DateRange } from "react-day-picker";
import type { BillingInterval } from "data/models";

dayjs.extend(quarterOfYear);

interface BillingPeriod {
  currentPeriodStart: number; // unix seconds
  renewalDate: number; // unix seconds
  cadence: BillingInterval;
}

interface UsageDateRangePickerProps {
  isFreemium: boolean;
  label: string;
  setStartTime: (time: number) => void;
  setEndTime: (time: number) => void;
  setLabel: (label: string) => void;
  billingPeriod?: BillingPeriod;
}

interface PresetShortcut {
  label: string;
  getValue: () => [Date, Date];
}

const buildBillingPeriodPresets = (
  billingPeriod?: BillingPeriod,
): PresetShortcut[] => {
  if (!billingPeriod) return [];

  const { currentPeriodStart, renewalDate, cadence } = billingPeriod;
  const presets: PresetShortcut[] = [];

  const periodStart = dayjs.unix(currentPeriodStart);
  const periodEnd = dayjs.unix(renewalDate);

  presets.push({
    label: "This Billing Period",
    getValue: () => [
      periodStart.startOf("day").toDate(),
      periodEnd.endOf("day").toDate(),
    ],
  });

  // Only offer "previous" if the sub has actually been running long enough
  // to have a completed prior period.
  const prevEnd = periodStart.subtract(1, "day").endOf("day");
  const prevStart = periodStart.subtract(1, cadence).startOf("day");
  if (prevStart.isBefore(prevEnd)) {
    presets.push({
      label: "Previous Billing Period",
      getValue: () => [prevStart.toDate(), prevEnd.toDate()],
    });
  }

  return presets;
};

const presetShortcuts: PresetShortcut[] = [
  {
    label: "Today",
    getValue: () => [
      dayjs().startOf("day").toDate(),
      dayjs().endOf("day").toDate(),
    ],
  },
  {
    label: "Yesterday",
    getValue: () => {
      const yesterday = dayjs().subtract(1, "day");
      return [
        yesterday.startOf("day").toDate(),
        yesterday.endOf("day").toDate(),
      ];
    },
  },
  {
    label: "Last 7 Days",
    getValue: () => [
      dayjs().subtract(6, "day").startOf("day").toDate(),
      dayjs().endOf("day").toDate(),
    ],
  },
  {
    label: "Last 14 Days",
    getValue: () => [
      dayjs().subtract(13, "day").startOf("day").toDate(),
      dayjs().endOf("day").toDate(),
    ],
  },
  {
    label: "Last 30 Days",
    getValue: () => [
      dayjs().subtract(29, "day").startOf("day").toDate(),
      dayjs().endOf("day").toDate(),
    ],
  },
  {
    label: "Last Month",
    getValue: () => {
      const lastMonth = dayjs().subtract(1, "month");
      return [
        lastMonth.startOf("month").toDate(),
        lastMonth.endOf("month").toDate(),
      ];
    },
  },
  {
    label: "Last Quarter",
    getValue: () => {
      const lastQuarter = dayjs().subtract(1, "quarter");
      return [
        lastQuarter.startOf("quarter").toDate(),
        lastQuarter.endOf("quarter").toDate(),
      ];
    },
  },
  {
    label: "Current Month",
    getValue: () => [
      dayjs().startOf("month").toDate(),
      dayjs().endOf("day").toDate(),
    ],
  },
  {
    label: "Current Quarter",
    getValue: () => [
      dayjs().startOf("quarter").toDate(),
      dayjs().endOf("day").toDate(),
    ],
  },
  {
    label: "Year to Date",
    getValue: () => [
      dayjs().startOf("year").toDate(),
      dayjs().endOf("day").toDate(),
    ],
  },
];

export const UsageDateRangePicker = ({
  isFreemium,
  label,
  setStartTime,
  setEndTime,
  setLabel,
  billingPeriod,
}: UsageDateRangePickerProps) => {
  const billingPresets = isFreemium
    ? []
    : buildBillingPeriodPresets(billingPeriod);
  const allPresets = [...billingPresets, ...presetShortcuts];
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    undefined,
  );

  const today = new Date();
  const thirtyDaysAgo = dayjs().subtract(30, "day").toDate();

  const applyRange = (from: Date, to: Date, rangeLabel: string) => {
    setSelectedRange({ from, to });
    setStartTime(dayjs(from).startOf("day").unix() * 1000);
    setEndTime(dayjs(to).endOf("day").unix() * 1000);
    setLabel(rangeLabel);
  };

  const handleCalendarSelect = (range: DateRange | undefined) => {
    setSelectedRange(range);
    if (range?.from && range?.to) {
      const startDay = dayjs(range.from).format("MMM DD");
      const endDay = dayjs(range.to).format("MMM DD");
      applyRange(range.from, range.to, `${startDay} - ${endDay}`);
    }
  };

  const handlePresetClick = (preset: PresetShortcut) => {
    const [from, to] = preset.getValue();
    applyRange(from, to, preset.label);
  };

  const disabledDays = isFreemium
    ? [{ before: thirtyDaysAgo }, { after: today }]
    : [{ after: today }];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="default">
          <CalendarDays />
          {label}
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto" align="end">
        <div className="flex">
          <div className="border-r w-max min-w-32 max-w-56">
            {allPresets.map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                type="button"
                className="w-full justify-start whitespace-nowrap text-sm"
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <div>
            <Calendar
              mode="range"
              selected={selectedRange}
              onSelect={handleCalendarSelect}
              numberOfMonths={1}
              disabled={disabledDays}
              captionLayout="dropdown"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
